import { NavLink } from "react-router-dom";
import { Loader2 } from "lucide-react";

export const PageHeader = ({ title, subtitle, actions, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6" data-testid={testid}>
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[#0B1530]">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const SubTabs = ({ tabs }) => (
  <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto" data-testid="sub-tabs">
    {tabs.map((t) => (
      <NavLink
        key={t.to}
        to={t.to}
        end
        data-testid={`subtab-${t.key}`}
        className={({ isActive }) =>
          `px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
            isActive ? "border-[#E8A33D] text-[#0B1530] font-semibold" : "border-transparent text-slate-500 hover:text-[#0B1530]"
          }`
        }
      >
        {t.label}
        {t.phase2 && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-400">Phase 2</span>}
      </NavLink>
    ))}
  </div>
);

export const Panel = ({ title, action, children, className = "", testid }) => (
  <section className={`bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_2px_rgba(11,21,48,0.04)] ${className}`} data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-[#0B1530]">{title}</h3>
        {action}
      </div>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const Empty = ({ icon: Icon, title, hint, action, testid }) => (
  <div className="flex flex-col items-center text-center py-10 px-4" data-testid={testid || "empty-state"}>
    {Icon && <div className="h-11 w-11 rounded-full bg-slate-100 flex items-center justify-center mb-3"><Icon className="h-5 w-5 text-slate-400" /></div>}
    <p className="text-sm font-medium text-slate-700">{title}</p>
    {hint && <p className="text-xs text-slate-500 mt-1 max-w-xs">{hint}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Loading = () => (
  <div className="flex items-center justify-center py-16 text-slate-400" data-testid="ws-loading">
    <Loader2 className="h-5 w-5 animate-spin" />
  </div>
);

export const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1.5">{label}</span>
    {children}
  </label>
);

export const NativeSelect = ({ value, onChange, options, placeholder, testid }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value || null)}
    data-testid={testid}
    className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1530]/20"
  >
    <option value="">{placeholder || "— None —"}</option>
    {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const Avatar = ({ name, size = "h-9 w-9" }) => {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className={`${size} rounded-full bg-[#0B1530] text-[#F3D7A6] flex items-center justify-center text-xs font-semibold shrink-0`}>
      {initials}
    </div>
  );
};
